import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, FileText, Calendar, User, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

/**
 * Affiche chronologiquement toutes les synthèses de RDV individuels
 * d'un bénéficiaire donné. Visible par les intervenants uniquement.
 */
const SynthesesBeneficiaire = ({ apprenantId }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ total: 0, syntheses: [], apprenant: null });

  useEffect(() => {
    if (!apprenantId) return;
    loadSyntheses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apprenantId]);

  const loadSyntheses = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/apprenants/${apprenantId}/syntheses`);
      setData(res.data);
    } catch (error) {
      toast.error('Erreur de chargement des synthèses');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-200 rounded-3xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-indigo-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
            <FileText className="w-6 h-6" />
            Historique des synthèses
            <Badge className="ml-2 bg-indigo-600 text-white">{data.total}</Badge>
          </CardTitle>
          {data.apprenant && (
            <p className="text-sm text-indigo-700 mt-1">
              {data.apprenant.prenom} {data.apprenant.nom}
            </p>
          )}
        </CardHeader>
      </Card>

      {data.total === 0 ? (
        <Card className="bg-white border-slate-200 rounded-3xl p-8 text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">Aucune synthèse pour le moment</p>
          <p className="text-sm text-slate-400 mt-1">
            Les synthèses des RDV individuels apparaîtront ici une fois saisies par les intervenants
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {data.syntheses.map((s) => (
            <Card key={s.id} className="bg-white border-slate-200 rounded-2xl hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-2 text-slate-900 font-semibold">
                      <Calendar className="w-4 h-4 text-indigo-600" />
                      {formatDate(s.date)}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {s.heure_debut} - {s.heure_fin}
                      </span>
                      {s.type_action && (
                        <Badge variant="outline" className="text-xs">
                          {s.type_action.replace(/_/g, ' ')}
                        </Badge>
                      )}
                    </div>
                    {s.titre && (
                      <p className="text-sm text-slate-700 mt-1 italic">{s.titre}</p>
                    )}
                  </div>
                  {s.synthese_redigee_par_nom && (
                    <div className="text-right text-xs text-slate-500">
                      <div className="flex items-center gap-1 justify-end">
                        <User className="w-3 h-3" />
                        {s.synthese_redigee_par_nom}
                      </div>
                      {s.synthese_date && (
                        <div className="mt-0.5">
                          {new Date(s.synthese_date).toLocaleDateString('fr-FR')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 whitespace-pre-wrap text-sm text-slate-800">
                  {s.synthese}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default SynthesesBeneficiaire;
